import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Flame, Timer, Trophy, Users } from "lucide-react";
import Seo, { breadcrumbSchema, faqSchema } from "../components/Seo";
import { Reveal, Eyebrow, BtnPrimary, BtnGhost, FaqList, CtaBand, Crumbs } from "../components/Shared";
import { LeadBand } from "../components/LeadForm";
import { HeroVideo } from "../components/HeroVideo";
import { HYROX, IMG, chalkitLink, SITE, NEXT_HYROX_BLOCK, HYROX_BLOCK_TEXT, HYROX_RACE_PAST } from "../data/mock";
import { QuickAnswer } from "../components/QuickAnswer";

const RACE_START = new Date("2026-10-08T08:00:00-04:00").getTime();
const RACE_END = new Date("2026-10-11T23:59:59-04:00").getTime();

function Countdown() {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (now > RACE_END) return null;
  const live = now >= RACE_START;
  const diff = Math.max(0, RACE_START - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return (
    <section className="bg-[#9D0423] py-8" data-testid="hyrox-countdown">
      <div className="max-w-7xl mx-auto px-6 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/70">// HYROX Boston · October 8–11</p>
          <h2 className="mt-1 font-heading font-extrabold uppercase text-2xl lg:text-3xl text-white tracking-tight">
            {live ? "Race weekend is live." : "The clock is running."}
          </h2>
        </div>
        {live ? (
          <p className="font-mono text-sm uppercase tracking-[0.2em] text-white">Go crush it, crew. 3-2-1 GO!</p>
        ) : (
          <div className="flex gap-3 sm:gap-4" aria-label={`${d} days ${h} hours ${m} minutes until HYROX Boston`}>
            {[[d, "Days"], [h, "Hrs"], [m, "Min"], [s, "Sec"]].map(([v, l]) => (
              <div key={l} className="w-[68px] sm:w-20 bg-[#0A0A0B] py-3 text-center">
                <div className="font-heading font-extrabold text-2xl sm:text-3xl text-white tabular-nums" data-testid={`countdown-${String(l).toLowerCase()}`}>
                  {String(v).padStart(2, "0")}
                </div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{l}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default function Hyrox() {
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "HYROX Training", path: "/hyrox" },
  ];

  const gymAddress = {
    "@type": "PostalAddress",
    streetAddress: SITE.address.street,
    addressLocality: SITE.address.city,
    addressRegion: SITE.address.state,
    postalCode: SITE.address.zip,
    addressCountry: "US",
  };

  const hyroxService = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "HYROX Training Boston — Boston Iron Grit",
    serviceType: "HYROX Race Training",
    description:
      "HYROX training in West Roxbury, Boston: dedicated club sessions covering compromised running, all 8 race stations, pacing strategy and 8-week race-prep blocks.",
    provider: { "@id": "https://bostonirongrittraining.com/#business" },
    areaServed: { "@type": "Place", name: "Boston, MA" },
    url: "https://bostonirongrittraining.com/hyrox",
  };

  const hyroxLocation = {
    "@context": "https://schema.org",
    "@type": "SportsActivityLocation",
    "@id": `${SITE.url}/hyrox#location`,
    name: "HYROX Training at Boston Iron Grit",
    sameAs: `${SITE.url}/#business`,
    url: `${SITE.url}/hyrox`,
    telephone: SITE.phone,
    address: gymAddress,
  };

  const hyroxCourse = {
    "@context": "https://schema.org",
    "@type": "Course",
    name: "HYROX Race Prep (8 weeks)",
    description:
      "An 8-week HYROX race-prep block at Boston Iron Grit in West Roxbury: race-specific station work, compromised running and pacing — classes Tue/Thu 6:30 AM, Saturday 9:30 AM Hybrid Core & Zone 2, and Sunday B.I.G. Engine.",
    provider: { "@id": `${SITE.url}/#business` },
    hasCourseInstance: {
      "@type": "CourseInstance",
      courseMode: "onsite",
      ...(HYROX_BLOCK_TEXT ? { startDate: NEXT_HYROX_BLOCK } : {}),
      location: { "@type": "Place", name: SITE.name, address: gymAddress },
    },
  };

  return (
    <>
      <Seo
        title="HYROX Training in Boston (West Roxbury) | Boston Iron Grit"
        description="Train for HYROX in Boston with coached race prep in West Roxbury: sleds, SkiErgs, compromised running and pacing. Classes Tue/Thu/Sat. Free goal review."
        path="/hyrox"
        schemas={[breadcrumbSchema(crumbs), hyroxService, hyroxLocation, hyroxCourse, faqSchema(HYROX.faqs)]}
      />

      {/* HERO */}
      <section className="relative min-h-[85vh] flex items-center bg-[#0A0A0B] overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMG.hyroxSandbag})` }} aria-hidden="true" />
        <HeroVideo base="/video/hero-conditioning-15s" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0B] via-[#0A0A0B]/80 to-[#0A0A0B]/30" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-transparent to-transparent" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 py-24 w-full">
          <Reveal>
            <Crumbs items={crumbs} />
            <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.3em] text-[#9D0423]">// The World Series of Fitness Racing</p>
            <h1 className="mt-4 font-heading font-extrabold uppercase text-5xl sm:text-6xl lg:text-[80px] text-white leading-[0.92] tracking-tight">
              HYROX Training<br /><span className="text-[#9D0423]">Boston.</span>
            </h1>
            <p className="mt-7 text-neutral-200 text-base lg:text-lg leading-relaxed max-w-2xl border-l-2 border-[#9D0423] pl-5">
              HYROX is an indoor fitness race: 8x 1km runs, each followed by a functional station. Every race worldwide
              is identical — so your time means something. Our HYROX Club in West Roxbury builds the engine, the
              strength and the pacing to get you across that finish line.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <BtnPrimary to="/get-started">Join HYROX Club</BtnPrimary>
              <BtnGhost to="/schedule">See Club Sessions</BtnGhost>
            </div>
            <div className="mt-12 flex flex-wrap gap-x-6 gap-y-3 font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-400">
              <span>8km total running</span><span className="text-neutral-700">/</span>
              <span>8 stations</span><span className="text-neutral-700">/</span>
              <span>Open · Pro · Doubles · Relay</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* QUICK ANSWER — directly under the hero */}
      <section className="bg-[#0A0A0B] py-8" data-testid="hyrox-quick-answer">
        <QuickAnswer>
          Where can I train for HYROX in Boston? Boston Iron Grit in West Roxbury runs a dedicated HYROX Club with sleds,
          SkiErgs and rowers: race-specific classes Tue/Thu 6:30 AM, a Saturday 9:30 AM Hybrid Core & Zone 2 session, and
          Sunday B.I.G. Engine. Members add HYROX for $50/month; new athletes can buy a 24-session punch card for $600.
        </QuickAnswer>
      </section>

      <Countdown />

      {/* WHAT IS HYROX */}
      <section className="bg-[#0D0D0F] border-y border-white/5 py-20 lg:py-28" data-testid="hyrox-what-is">
        <div className="max-w-4xl mx-auto px-6">
          <Reveal>
            <Eyebrow>The Definitive Guide</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-4xl lg:text-5xl text-white leading-[0.95] tracking-tight">
              What is HYROX,<br /><span className="text-neutral-600">exactly?</span>
            </h2>
            <div className="mt-8 space-y-5 text-[15px] text-neutral-300 leading-[1.85]">
              <p>
                HYROX is an indoor fitness race founded in Germany in 2017 that has grown into the largest mass-participation
                fitness event in the world, with races in dozens of cities and hundreds of thousands of finishers each season.
                The format never changes: you run 1 kilometer, complete a functional workout station, and repeat that couplet
                eight times — 8 kilometers of running and 8 stations, always in the same order, always with the same standards.
              </p>
              <p>
                That standardization is the genius of it. A marathon course changes city to city; a CrossFit competition changes
                workout to workout. HYROX is identical in Boston, Berlin and Hong Kong — so your finish time is a true, portable
                benchmark of your fitness that you can test against yourself season after season, and against every athlete on earth.
              </p>
              <p>
                The fitness it demands is hybrid: a big aerobic engine to sustain roughly an hour or more of continuous work, plus
                strength endurance for sleds, carries, lunges and wall balls. There are no barbells, no gymnastics and no highly
                technical movements — which is exactly why almost anyone who trains consistently can finish one, and why it has
                pulled runners, CrossFitters and everyday gym-goers onto the same start line.
              </p>
              <p>
                If you train at a gym like ours, you're closer than you think. CrossFit classes build the broad engine; our HYROX
                Club sessions sharpen it into race-specific capacity — compromised running, station pacing and the mental map of
                what minute 55 feels like.{" "}
                {HYROX_RACE_PAST ? (
                  <>Next race season: <strong className="text-white">training starts now</strong> — the club trains year-round.</>
                ) : (
                  <>The next home race is <strong className="text-white">HYROX Boston, October 8–11</strong>,
                and it's the target our whole club is training toward.</>
                )}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* THE RACE FORMAT */}
      <section className="bg-[#0A0A0B] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <Eyebrow>The Race Format</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-4xl lg:text-5xl text-white leading-[0.95] tracking-tight">
              Run 1km. Hit a station.<br /><span className="text-neutral-600">Repeat eight times.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
            {HYROX.stations.map((s, i) => (
              <Reveal key={s.num} delay={(i % 4) * 70}>
                <div className="group bg-[#0D0D0F] p-7 h-full hover:bg-[#121214] transition-colors relative overflow-hidden">
                  <div className="absolute -top-3 -right-2 font-heading font-extrabold text-[80px] leading-none text-white/[0.04] group-hover:text-[#9D0423]/10 transition-colors select-none">
                    {s.num}
                  </div>
                  <div className="relative">
                    <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#9D0423]">Station {s.num}</div>
                    <h3 className="mt-3 font-heading font-bold uppercase text-xl text-white tracking-tight">{s.name}</h3>
                    <p className="mt-2 text-[13px] text-neutral-400 leading-relaxed">{s.detail}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500">
            * Each station is preceded by a 1km run — 8km of running total.
          </p>
        </div>
      </section>

      {/* DIVISIONS */}
      <section className="bg-[#0D0D0F] border-y border-white/5 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <img src={IMG.hyroxRun} alt="Coach Miguel coaching wall balls — the final HYROX station — at Boston Iron Grit" className="w-full h-[460px] object-cover" loading="lazy" />
          </Reveal>
          <Reveal delay={120}>
            <Eyebrow>Divisions</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-4xl lg:text-5xl text-white leading-[0.95] tracking-tight">
              There's a lane<br /><span className="text-neutral-600">for every athlete.</span>
            </h2>
            <div className="mt-8 space-y-4">
              {HYROX.divisions.map((d, i) => (
                <div key={d.name} className="flex items-start gap-4 p-5 bg-[#121214] border border-white/5 hover:border-[#9D0423]/40 transition-colors">
                  <div className="shrink-0 mt-0.5">
                    {[<Flame key="f" size={18} />, <Trophy key="t" size={18} />, <Users key="u" size={18} />, <Timer key="m" size={18} />][i]}
                  </div>
                  <div>
                    <h3 className="font-heading font-bold uppercase text-base text-white tracking-tight">{d.name}</h3>
                    <p className="mt-1 text-[13px] text-neutral-400 leading-relaxed">{d.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* HOW WE TRAIN */}
      <section className="bg-[#0A0A0B] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <Eyebrow>How We Train It</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-4xl lg:text-5xl text-white leading-[0.95] tracking-tight max-w-3xl">
              From first race to<br /><span className="text-neutral-600">personal best.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-2 gap-5">
            {HYROX.program.map((p, i) => (
              <Reveal key={p.name} delay={(i % 2) * 90}>
                <div className="flex items-start gap-6 p-7 bg-[#121214] border border-white/5 hover:border-[#9D0423]/40 transition-colors h-full">
                  <div className="font-heading font-extrabold text-4xl text-white/10 shrink-0">{String(i + 1).padStart(2, "0")}</div>
                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="font-heading font-bold uppercase text-xl text-white tracking-tight">{p.name}</h3>
                      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#E56A79] bg-[#9D0423]/10 border border-[#9D0423]/25 px-2.5 py-1">{p.when}</span>
                    </div>
                    <p className="mt-2.5 text-sm text-neutral-400 leading-relaxed">{p.detail}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={100}>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <BtnPrimary href={chalkitLink("hyroxPunch24")}>Join via ChalkIt Pro</BtnPrimary>
              <BtnGhost to="/classes/crossfit">Pair It With CrossFit</BtnGhost>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHO COACHES HYROX */}
      <section className="bg-[#0D0D0F] border-y border-white/5 py-16 lg:py-20" data-testid="hyrox-coaches">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <Eyebrow>Your Coaches</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-3xl lg:text-4xl text-white tracking-tight mb-10">
              Who coaches HYROX here.
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-3 gap-5">
            {[
              { name: "Tina Ramos", creds: "CF-L2 · Owner & Head Coach", when: "HYROX Classes · Tue & Thu 6:30 AM", slug: "tina-ramos", photo: "/images/coaches/tina.jpg" },
              { name: "Kate", creds: "HYROX Coach", when: "HYROX Classes · Tue & Thu 6:30 AM", slug: null, photo: null },
              { name: "Miguel Claravall", creds: "CF-L3 · USAW-L1", when: "Hybrid Core & Zone 2 · Sat 9:30 AM", slug: "miguel-claravall", photo: "/images/coaches/miguel.jpg" },
            ].map((c, i) => {
              const card = (
                <div className="flex items-center gap-5 p-5 bg-[#121214] border border-white/5 hover:border-[#9D0423]/40 transition-colors h-full">
                  {c.photo ? (
                    <img src={c.photo} alt={`Coach ${c.name} — HYROX coach at Boston Iron Grit`} className="w-16 h-16 object-cover shrink-0" loading="lazy" />
                  ) : (
                    <div className="w-16 h-16 shrink-0 bg-[#0A0A0B] border border-white/10 flex items-center justify-center font-heading font-extrabold text-2xl text-[#9D0423]" aria-hidden="true">
                      {c.name[0]}
                    </div>
                  )}
                  <div>
                    <h3 className="font-heading font-bold uppercase text-lg text-white tracking-tight">
                      {c.name}
                      {c.slug && <ArrowRight size={14} className="inline ml-2 text-[#9D0423]" />}
                    </h3>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[#E56A79]">{c.creds}</p>
                    <p className="mt-1.5 text-[12px] text-neutral-400">{c.when}</p>
                  </div>
                </div>
              );
              return (
                <Reveal key={c.name} delay={i * 80}>
                  {c.slug ? (
                    <Link to={`/coaches/${c.slug}`} data-testid={`hyrox-coach-${c.slug}`} className="block h-full">{card}</Link>
                  ) : (
                    <div data-testid="hyrox-coach-kate" className="h-full">{card}</div>
                  )}
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PACING & RACE STRATEGY */}
      <section className="bg-[#0D0D0F] border-y border-white/5 py-20 lg:py-28" data-testid="hyrox-strategy">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <Eyebrow>Race Strategy</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-4xl lg:text-5xl text-white leading-[0.95] tracking-tight">
              Pacing wins races.<br /><span className="text-neutral-600">Ego loses them.</span>
            </h2>
          </Reveal>
          <div className="mt-10 grid lg:grid-cols-2 gap-12">
            <Reveal>
              <div className="space-y-5 text-[15px] text-neutral-300 leading-[1.85]">
                <p>
                  The defining skill of HYROX is <strong className="text-white">compromised running</strong> — running on legs that
                  just pushed a sled, then working a station on lungs that just ran. Your open 1km pace is irrelevant; what matters
                  is the pace you can hold on the sixth run. We train it deliberately: intervals off the SkiErg, runs off the rower,
                  wall balls on jelly legs.
                </p>
                <p>
                  The classic first-race mistake is banking time early. The athletes who blow up at the sandbag lunges are almost
                  always the ones who ran their first two kilometers 30 seconds too fast. Race like our coaches teach it: first two
                  runs feel embarrassingly easy, steady effort through the sleds, then spend whatever is left from the lunges home.
                </p>
                <p>
                  <strong className="text-white">The wall lives at stations 7 and 8.</strong> A hundred meters of sandbag lunges
                  followed by 75–100 wall balls is where every pacing decision you made comes due. Break the wall balls into planned
                  sets from rep one — 15s or 20s with three breaths between — instead of going to failure and staring at the ceiling.
                </p>
              </div>
            </Reveal>
            <Reveal delay={100}>
              <div className="grid gap-px bg-white/5 border border-white/5">
                {[
                  ["Sub 60:00", "Elite men's territory — top of the world rankings. Elite women run near 65:00."],
                  ["65:00 – 75:00", "Highly competitive age-group racing. Podium pace at most events."],
                  ["75:00 – 90:00", "A strong amateur time — realistic for consistent club athletes with a focused prep block."],
                  ["90:00 – 105:00", "The typical first-race window. Finish here and you have a benchmark to demolish next season."],
                ].map(([time, note]) => (
                  <div key={time} className="bg-[#0A0A0B] p-6 flex items-start gap-5">
                    <span className="font-heading font-extrabold text-2xl text-[#9D0423] whitespace-nowrap w-40 shrink-0">{time}</span>
                    <span className="text-[13px] text-neutral-400 leading-relaxed">{note}</span>
                  </div>
                ))}
              </div>
              <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.16em] text-neutral-600">Open division reference times — doubles and relay run faster.</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* COMMON MISTAKES */}
      <section className="bg-[#0A0A0B] py-20 lg:py-28" data-testid="hyrox-mistakes">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <Eyebrow>Learn It Here, Not On Race Day</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-4xl lg:text-5xl text-white leading-[0.95] tracking-tight max-w-3xl">
              Five mistakes first-time<br /><span className="text-neutral-600">racers make.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              ["Running too fast, too early", "Adrenaline makes kilometer one feel free. It isn't — you pay for it at the lunges. Negative-split the race: every run after the fourth should feel controlled, not desperate."],
              ["Ignoring the sleds", "The sled push and pull are where untrained racers lose 3–5 minutes and their legs. Sled work at race weight is in our club programming every single week."],
              ["Never training compromised", "Interval runs and station work practiced separately don't prepare you for doing them together. Every HYROX Club session chains them the way the race does."],
              ["No wall ball plan", "Up to 100 wall balls at the finish, judged to standard. Going to failure on set one adds minutes. Practice planned sets with short rests until the math is automatic."],
              ["New shoes, new fuel, race day", "Nothing new on race day. Wear the grippy shoes you've pushed sleds in, and practice your carbs and caffeine strategy during training simulations, not at the venue."],
              ["Skipping the roxzone", "The transition zone between stations is part of the race — sloppy, wandering transitions quietly add 5+ minutes. We rehearse tight, purposeful transitions in every simulation."],
            ].map(([title, body], i) => (
              <Reveal key={title} delay={(i % 3) * 80}>
                <div className="p-7 bg-[#121214] border border-white/5 hover:border-[#9D0423]/40 transition-colors h-full">
                  <span className="font-heading font-extrabold text-3xl text-white/10">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 font-heading font-bold uppercase text-lg text-white tracking-tight">{title}</h3>
                  <p className="mt-2.5 text-[13px] text-neutral-400 leading-relaxed">{body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* RACE WEEKEND */}
      <section className="bg-[#0D0D0F] border-y border-white/5 py-20 lg:py-28" data-testid="hyrox-race-weekend">
        <div className="max-w-4xl mx-auto px-6">
          <Reveal>
            <Eyebrow>Race Weekend</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-4xl lg:text-5xl text-white leading-[0.95] tracking-tight">
              {HYROX_RACE_PAST ? (
                <>Next race season:<br /><span className="text-[#9D0423]">training starts now.</span></>
              ) : (
                <>HYROX Boston:<br /><span className="text-[#9D0423]">October 8–11.</span></>
              )}
            </h2>
            <div className="mt-8 space-y-5 text-[15px] text-neutral-300 leading-[1.85]">
              <p>
                Race weekend runs in waves across four days, with Open, Pro, Doubles and Relay divisions and age-group categories,
                so there is genuinely a start line for every athlete in the gym. Doubles — where two athletes split the station work
                but both run every kilometer — is our favorite recommendation for first-timers who want the experience with a
                partner carrying half the load.
              </p>
              <p>
                Plan to arrive at least 90 minutes before your wave: check-in, chip pickup, warm-up and finding your corral all eat
                time. Spectators are welcome and the venue is built for them — the entire race happens inside one arena, so your
                people can follow you from the SkiErg to the wall balls shouting the whole way. It's the closest most of us will get
                to racing in a stadium.
              </p>
              <p>
                Our club goes as a crew — shared warm-ups, coaches at the barriers, and a plan for every athlete's target time built
                during the 8-week prep block. When the season's race passes, HYROX Club keeps running year-round: the format is also
                simply an outstanding way to train, race or no race.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* OFFERS & PRICING */}
      <section className="bg-[#0A0A0B] py-20 lg:pb-28" data-testid="hyrox-offers">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <Eyebrow>Ways To Train</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-4xl lg:text-5xl text-white leading-[0.95] tracking-tight">
              HYROX offers<br />
              <span className="text-neutral-600">
                {HYROX_RACE_PAST ? "for your next race season." : `for ${HYROX.race.name}, ${HYROX.race.dates}.`}
              </span>
            </h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-3 gap-5">
            {HYROX.offers.map((o, i) => (
              <Reveal key={o.name} delay={i * 90}>
                <div
                  data-testid={`hyrox-offer-${o.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`p-7 h-full transition-all duration-300 hover:translate-y-[-4px] ${
                    i === 2 ? "bg-[#141416] border-2 border-[#9D0423]" : "bg-[#121214] border border-white/8 hover:border-white/20"
                  }`}
                >
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-neutral-500">{o.name}</p>
                  <div className="mt-3 font-heading font-extrabold text-4xl text-white">{o.price}</div>
                  <p className="mt-3 text-[13px] text-neutral-400 leading-relaxed">{o.detail}</p>
                  <a
                    href={chalkitLink(o.chalkitKey)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`hyrox-offer-cta-${o.chalkitKey}`}
                    className={`mt-6 inline-flex items-center justify-center w-full font-mono font-semibold text-[12px] uppercase tracking-[0.14em] px-5 py-3.5 transition-colors ${
                      i === 2 ? "bg-[#9D0423] hover:bg-[#7E031C] text-white" : "border border-white/20 hover:border-white/60 text-white"
                    }`}
                  >
                    Get Started
                  </a>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.14em] text-neutral-500">{HYROX.race.prep}</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#0D0D0F] border-y border-white/5 py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-6">
          <Reveal>
            <FaqList faqs={HYROX.faqs} title="HYROX — FAQs" />
          </Reveal>
          <div className="mt-12">
            <Link to="/blog" className="inline-flex items-center gap-2 font-mono text-[12px] uppercase tracking-[0.16em] text-neutral-300 hover:text-white transition-colors">
              More training guides on the blog <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <LeadBand
        formName="hyrox"
        programInterest="HYROX Training"
        title="Ask a coach about HYROX."
        sub="Racing soon or just HYROX-curious — tell us where you're at and a coach will map your first step, including the 8-week prep block."
      />

      <CtaBand
        title="Ready to race?"
        sub="Book a free goal review and we'll map your first (or fastest) HYROX — including an 8-week prep block built around your event date."
      />
    </>
  );
}
